/**
 * SupplierFeeManagementPage Component
 * 수수료 관리 (협력사) - 기본 수수료 설정 및 라이더별 수수료 관리
 */
import React, { useEffect, useState, useCallback } from "react";
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import { usePagination } from "@/shared/hooks/usePagination";
import { getItem } from "@/shared/utils/storage";
import { useUserInfo } from "@/shared/hooks/useCrudOperations";
import { loadFilters, saveFilters } from '@/shared/utils/filterStorage';
import { riderFeeApi, RiderFeeDto } from "../api/RiderFeeApi";
import { useSupplierFeeHandlers } from "../hooks/useSupplierFeeHandlers";
import { RiderCommissionRow, FILTER_STORAGE_KEY, ITEMS_PER_PAGE } from "../lib/supplierFeeHelpers";
import Loading from '@/shared/components/feedback/Loading';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import DefaultFeeSection from './DefaultFeeSection';
import FeeFilterSection from './FeeFilterSection';
import FeeTableRow from './FeeTableRow';
import HistoryModal from './HistoryModal';
import '@/shared/styles/common-table.css';

interface SupplierFeeManagementProps {
    selectedSupplierId?: string;
    onUnsavedChanges?: (hasChanges: boolean) => void;
}

const SupplierFeeManagementPage: React.FC<SupplierFeeManagementProps> = ({ selectedSupplierId, onUnsavedChanges }) => {
    // ========================================
    // 1. Hooks - 기본
    // ========================================
    const { showToast } = useToast();
    const { getUserInfo } = useUserInfo();

    // ========================================
    // 2. State - 데이터 상태
    // ========================================
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);
    const [commissionData, setCommissionData] = useState<RiderCommissionRow[]>([]);
    const [filteredData, setFilteredData] = useState<RiderCommissionRow[]>([]);

    // ========================================
    // 3. State - 필터 상태
    // ========================================
    const [filters, setFilters] = useState(() => {
        const saved = loadFilters<{ riderName: string; riderId: string; statusFilter: string }>(FILTER_STORAGE_KEY);
        return saved || { riderName: '', riderId: '', statusFilter: '' };
    });
    const [statusFilter, setStatusFilter] = useState<string>(filters.statusFilter || '');

    // ========================================
    // 4. 상수 - 권한 체크
    // ========================================
    const isHeadquarters = (): boolean => {
        const userType = getItem<string>('USER_TYPE');
        return userType !== 'SUPPLIER';
    };
    const isReadOnly = isHeadquarters();

    // ========================================
    // 5. Hooks - 페이지네이션
    // ========================================
    const {
        currentPage,
        totalPages,
        currentItems,
        goToPage,
        resetPagination
    } = usePagination({ items: filteredData, itemsPerPage: ITEMS_PER_PAGE.DEFAULT });

    // ========================================
    // 6. 데이터 로드 함수
    // ========================================
    /**
     * 라이더 수수료 목록 조회
     */
    const fetchRiderFees = useCallback(async () => {
        try {
            setLoading(true);
            setError(false);

            const userInfo = getUserInfo();
            if (!userInfo) {
                setLoading(false);
                return;
            }

            const targetSupplierId = selectedSupplierId || userInfo.suppId;
            if (!targetSupplierId) {
                showToast('협력사 정보를 찾을 수 없습니다.', { type: ToastType.ERROR });
                setLoading(false);
                return;
            }

            const riderFees: RiderFeeDto[] = await riderFeeApi.getRiderFees(targetSupplierId);

            const rowData: RiderCommissionRow[] = riderFees.map(fee => ({
                riderId: fee.riderId,
                riderName: fee.riderName,
                commissionType: fee.commissionType || '',
                amount: fee.amount ? fee.amount.toString() : '',
                applyDate: fee.applyDate || '',
                isEdited: false,
                contStatus: fee.contStatus || ''
            }));

            // 계약 중인 라이더가 먼저, 계약 종료 라이더가 뒤에 오도록 정렬
            rowData.sort((a, b) => {
                const aUnderContract = a.contStatus === 'UNDER_CONTRACT';
                const bUnderContract = b.contStatus === 'UNDER_CONTRACT';
                if (aUnderContract && !bUnderContract) return -1;
                if (!aUnderContract && bUnderContract) return 1;
                return 0;
            });

            setCommissionData(rowData);
            setLoading(false);
        } catch {
            setError(true);
            showToast('라이더 수수료 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            setLoading(false);
        }
    }, [selectedSupplierId]);

    /**
     * 재시도 핸들러
     */
    const handleRetry = useCallback(() => {
        fetchRiderFees();
    }, [fetchRiderFees]);

    // ========================================
    // 7. Hooks - 핸들러
    // ========================================
    const {
        showHistoryModal,
        selectedRider,
        historyData,
        handleCommissionTypeChange,
        handleAmountChange,
        handleSave,
        handleShowHistory,
        handleCloseHistoryModal
    } = useSupplierFeeHandlers({
        selectedSupplierId,
        commissionData,
        setCommissionData,
        fetchRiderFees
    });

    // ========================================
    // 8. Side Effects
    // ========================================
    // 초기 데이터 로드 및 협력사 변경 시 재조회
    useEffect(() => {
        fetchRiderFees();
    }, [fetchRiderFees]);

    // commissionData가 변경되면 filteredData 업데이트
    useEffect(() => {
        const savedFilters = loadFilters<{ riderName: string; riderId: string; statusFilter: string }>(FILTER_STORAGE_KEY);
        if (savedFilters && commissionData.length > 0) {
            let filtered = [...commissionData];

            if (savedFilters.riderName) {
                filtered = filtered.filter(row =>
                    row.riderName.toLowerCase().includes(savedFilters.riderName.toLowerCase())
                );
            }
            if (savedFilters.riderId) {
                filtered = filtered.filter(row =>
                    row.riderId.toLowerCase().includes(savedFilters.riderId.toLowerCase())
                );
            }
            if (savedFilters.statusFilter) {
                filtered = filtered.filter(row => row.contStatus === savedFilters.statusFilter);
            }

            setFilteredData(filtered);
        } else {
            setFilteredData(commissionData);
        }
    }, [commissionData]);

    // 수정 여부를 부모 컴포넌트에 알림
    useEffect(() => {
        const hasEdited = commissionData.some(row => row.isEdited);
        onUnsavedChanges?.(hasEdited);
    }, [commissionData, onUnsavedChanges]);

    // ========================================
    // 9. Event Handlers - 로컬
    // ========================================
    /**
     * 일괄 저장 핸들러
     */
    const handleBatchSave = async () => {
        const editedRows = commissionData.filter(row => row.isEdited);
        
        if (editedRows.length === 0) {
            showToast('수정된 항목이 없습니다.', { type: ToastType.WARNING });
            return;
        }

        const userInfo = getUserInfo();
        if (!userInfo) {
            showToast('사용자 정보를 찾을 수 없습니다.', { type: ToastType.ERROR });
            return;
        }

        const targetSupplierId = selectedSupplierId || userInfo.suppId;
        if (!targetSupplierId) {
            showToast('협력사 정보를 찾을 수 없습니다.', { type: ToastType.ERROR });
            return;
        }

        // 유효성 검사
        const invalidRows = editedRows.filter(row => {
            if (!row.commissionType) return true;
            
            const isFixed = row.commissionType === 'PER_CASE_FIXED' || row.commissionType === 'PER_DAY_FIXED';
            const amount = parseFloat(row.amount);
            
            if (isNaN(amount) || amount <= 0) return true;
            if (!isFixed && amount > 100) return true;
            
            return false;
        });

        if (invalidRows.length > 0) {
            showToast(`유효하지 않은 데이터가 있습니다. (${invalidRows.length}건)`, { type: ToastType.WARNING });
            return;
        }

        if (!window.confirm(`${editedRows.length}건의 수수료를 일괄 저장하시겠습니까?`)) {
            return;
        }

        try {
            setLoading(true);

            // 저장 요청 데이터 생성
            const requests = editedRows.map(row => ({
                suppId: targetSupplierId,
                riderId: row.riderId,
                commissionType: row.commissionType,
                amount: parseFloat(row.amount),
                applyTiming: 'IMMEDIATE',
                userId: userInfo.userId
            }));

            const result = await riderFeeApi.saveRiderFeesBatch(requests);
            showToast(result, { type: ToastType.SUCCESS });

            // 목록 새로고침
            await fetchRiderFees();
        } catch {
            showToast('일괄 저장에 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setLoading(false);
        }
    };

    /**
     * 검색 처리
     */
    const handleSearch = () => {
        let filtered = [...commissionData];

        if (filters.riderName) {
            filtered = filtered.filter(row =>
                row.riderName.toLowerCase().includes(filters.riderName.toLowerCase())
            );
        }
        if (filters.riderId) {
            filtered = filtered.filter(row =>
                row.riderId.toLowerCase().includes(filters.riderId.toLowerCase())
            );
        }
        if (statusFilter) {
            filtered = filtered.filter(row => row.contStatus === statusFilter);
        }

        setFilteredData(filtered);
        resetPagination();
        saveFilters(FILTER_STORAGE_KEY, { ...filters, statusFilter });
    };

    /**
     * 빠른 필터 차리 (상태 버튼 클릭 시 즉시 필터링)
     */
    const handleQuickFilter = useCallback((status: string) => {
        setStatusFilter(status);
        let filtered = [...commissionData];

        if (filters.riderName) {
            filtered = filtered.filter(row =>
                row.riderName.toLowerCase().includes(filters.riderName.toLowerCase())
            );
        }
        if (filters.riderId) {
            filtered = filtered.filter(row =>
                row.riderId.toLowerCase().includes(filters.riderId.toLowerCase())
            );
        }
        if (status) {
            filtered = filtered.filter(row => row.contStatus === status);
        }

        setFilteredData(filtered);
        resetPagination();
        saveFilters(FILTER_STORAGE_KEY, { ...filters, statusFilter: status });
    }, [commissionData, filters, resetPagination]);

    // ========================================
    // 10. Render - 로딩 상태
    // ========================================
    if (loading) {
        return <Loading message="수수료 데이터를 불러오는 중..." />;
    }

    // ========================================
    // 11. Render - 에러 상태
    // ========================================
    if (error) {
        return (
            <div className="supplier-selection-page">
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px',
                    backgroundColor: 'var(--color-white)',
                    border: '1px solid var(--color-gray-200)',
                    borderRadius: 'var(--border-radius-lg)',
                    padding: '40px'
                }}>
                    <i className="fas fa-exclamation-triangle" style={{
                        fontSize: '3rem',
                        color: 'var(--color-gray-400)',
                        marginBottom: '20px'
                    }}></i>
                    <p style={{
                        fontSize: '1.1rem',
                        color: 'var(--color-gray-600)',
                        marginBottom: '20px',
                        textAlign: 'center'
                    }}>
                        수수료 데이터를 불러오는데 실패했습니다.<br />
                        네트워크 연결을 확인하고 다시 시도해주세요.
                    </p>
                    <button
                        className="search-btn"
                        onClick={handleRetry}
                        style={{ marginLeft: 0 }}
                    >
                        <i className="fas fa-redo"></i>
                        <span>다시 시도</span>
                    </button>
                </div>
            </div>
        );
    }

    // ========================================
    // 12. Render - 메인 UI
    // ========================================
    return (
        <div className="supplier-selection-page">
            {/* ========== 기본 수수료 설정 섹션 (신규 추가) ========== */}
            <DefaultFeeSection 
                selectedSupplierId={selectedSupplierId}
                isReadOnly={isReadOnly}
            />

            {/* 본사 계정 안내 메시지 */}
            {isReadOnly && (
                <div style={{
                    padding: '12px 16px',
                    marginBottom: '16px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '4px',
                    color: '#856404',
                    fontSize: '0.875rem'
                }}>
                    <i className="fas fa-info-circle" style={{ marginRight: '8px' }}></i>
                    본사 계정은 수수료 설정을 수정할 수 없습니다. 협력사 계정으로 로그인하여 수정해주세요.
                </div>
            )}

            {/* ========== 구분선 ========== */}
            <div style={{
                marginBottom: '24px',
                borderBottom: '2px solid var(--color-gray-300)'
            }}></div>

            {/* 필터 섹션 */}
            <FeeFilterSection
                filters={filters}
                statusFilter={statusFilter}
                onFilterChange={setFilters}
                onSearch={handleSearch}
                onQuickFilter={handleQuickFilter}
            />

            {/* 일괄 저장 버튼 */}
            {!isReadOnly && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginBottom: '16px',
                    padding: '12px 16px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '8px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ 
                            fontSize: '0.875rem', 
                            color: '#6c757d',
                            fontWeight: '500'
                        }}>
                            <i className="fas fa-edit" style={{ marginRight: '6px', color: '#ffc107' }}></i>
                            수정된 항목: <strong style={{ color: '#dc3545' }}>
                                {commissionData.filter(row => row.isEdited).length}건
                            </strong>
                        </span>
                        <button
                            onClick={handleBatchSave}
                            style={{
                                backgroundColor: commissionData.some(row => row.isEdited) ? '#04B374' : '#6c757d',
                                color: 'white',
                                border: 'none',
                                padding: '8px 20px',
                                borderRadius: '6px',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                cursor: commissionData.some(row => row.isEdited) ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                boxShadow: '0 2px 4px rgba(40, 167, 69, 0.2)',
                                opacity: commissionData.some(row => row.isEdited) ? 1 : 0.6
                            }}
                            disabled={loading || !commissionData.some(row => row.isEdited)}
                        >
                            <i className="fas fa-save"></i>
                            전체 일괄 저장
                        </button>
                    </div>
                </div>
            )}

            {/* 테이블 섹션 */}
            <div className="table-container" style={{ overflowX: 'auto' }}>
                <table className="supplier-selection-table" style={{ minWidth: '800px' }}>
                    <thead>
                        <tr>
                            <th style={{ width: '60px', minWidth: '60px' }}>NO</th>
                            <th style={{ width: '100px', minWidth: '100px' }}>라이더ID</th>
                            <th style={{ width: '120px', minWidth: '120px' }}>라이더명</th>
                            <th style={{ width: '120px', minWidth: '120px' }}>수수료구분</th>
                            <th style={{ width: '100px', minWidth: '100px' }}>금액</th>
                            <th style={{ width: '120px', minWidth: '120px' }}>적용일</th>
                            <th style={{ width: '100px', minWidth: '100px' }}>수수료변경<br/>히스토리</th>
                            <th style={{ width: '80px', minWidth: '80px' }}>저장</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems.length === 0 ? (
                            <tr>
                                <td colSpan={8} className="text-center text-gray-600">
                                    조회된 라이더가 없습니다.
                                </td>
                            </tr>
                        ) : (
                            currentItems.map((row, index) => (
                                <FeeTableRow
                                    key={row.riderId}
                                    row={row}
                                    index={index}
                                    currentPage={currentPage}
                                    isReadOnly={isReadOnly}
                                    onCommissionTypeChange={handleCommissionTypeChange}
                                    onAmountChange={handleAmountChange}
                                    onShowHistory={handleShowHistory}
                                    onSave={handleSave}
                                />
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* 페이지네이션 */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
            />

            {/* 수수료 변경 히스토리 모달 */}
            <HistoryModal
                show={showHistoryModal}
                riderName={selectedRider}
                historyData={historyData}
                onClose={handleCloseHistoryModal}
            />
        </div>
    );
};

export default SupplierFeeManagementPage;
